import { useSuspenseQuery } from '@tanstack/react-query'
import { fetchAuthSession } from 'aws-amplify/auth'
import { jwtDecode } from 'jwt-decode'

import { logger } from 'src/lib/logger'
import { queries } from 'src/lib/query-keys'
import { getTokens } from 'src/lib/token-provider'

import { useOnboardingStatus } from './use-get-onboarding-status'

interface User {
  sub: string
  username?: string
  email?: string
  phone_number?: string
  'custom:onboardingComplete'?: string
  'cognito:groups'?: string[]
}

export const getCurrentAuth = async (): Promise<User | null> => {
  try {
    // Check local storage for tokens.
    const tokens = getTokens()
    if (tokens) {
      return jwtDecode(tokens.idToken) as User
    }

    const session = await fetchAuthSession({ forceRefresh: true })

    const user = session.tokens?.idToken ? (session.tokens.idToken.payload as User) : null

    return user
  } catch (error) {
    logger.error('❌ Error fetching current user:', error)
    return null
  }
}

const useAuth = () => {
  return useSuspenseQuery({
    ...queries.auth.session,
    queryFn: getCurrentAuth,
    staleTime: 0,
  })
}

export const useAuthState = () => {
  const { data: auth } = useAuth()
  const { data: onboardingStatus } = useOnboardingStatus()

  // DO NOT TOUCH THIS CODE
  const isSignedIn = !!auth && auth['cognito:groups']?.some((group) => group.toLowerCase() === 'user')
  // DO NOT TOUCH THIS CODE
  const isConsumerSignedIn = !!auth && auth['cognito:groups']?.some((group) => group.toLowerCase() === 'consumer')

  // Find onboarding status
  let needsOnboarding = false
  if (onboardingStatus) {
    needsOnboarding = onboardingStatus.onboardingComplete === false
  } else {
    needsOnboarding = auth?.['custom:onboardingComplete'] === 'false'
  }

  return {
    isSignedIn,
    isConsumerSignedIn,
    currentAuth: auth,
    needsOnboarding,
  }
}
